import { api } from "./api";
import type {
  PaginatedResponse,
  PartABPlanCoverageType,
  PartCDPlanCoverageType,
} from "types/DataTypes";

type GetPartABPlanCoveragesArgs = {
  file_id: number | string;
};

type GetPartCDPlanCoveragesArgs = {
  file_id: number | string;
};

type CreatePartABPlanCoverageArgs = {
  data: unknown;
};

type PatchPartABPlanCoverageArgs = {
  id: string | number;
  data: unknown;
};

type DeletePartABPlanCoverageArgs = {
  id: string | number;
};

type CreatePartCDPlanCoverageArgs = {
  data: unknown;
};

type PatchPartCDPlanCoverageArgs = {
  id: string | number;
  data: unknown;
};

type DeletePartCDPlanCoverageArgs = {
  id: string | number;
};

export const partCoverageAPI = api.injectEndpoints({
  endpoints: (build) => ({
    getPartABPlanCoverages: build.query<
      PaginatedResponse<PartABPlanCoverageType>,
      GetPartABPlanCoveragesArgs
    >({
      query: ({ file_id }) => ({
        url: `api/v1/part-a-b-plan-coverage/?file_id=${file_id}`,
        method: "GET",
      }),
      providesTags: ["PlanCoverage"],
    }),
    getPartCDPlanCoverages: build.query<
      PaginatedResponse<PartCDPlanCoverageType>,
      GetPartCDPlanCoveragesArgs
    >({
      query: ({ file_id }) => ({
        url: `api/v1/part-c-d-plan-coverage/?file_id=${file_id}`,
        method: "GET",
      }),
      providesTags: ["PlanCoverage"],
    }),
    createPartABPlanCoverage: build.mutation<
      unknown,
      CreatePartABPlanCoverageArgs
    >({
      query: ({ data }) => ({
        url: `api/v1/part-a-b-plan-coverage/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        "MSA",
        "MSAValidation",
        "MCP",
        "LienFile",
        "PlanCoverage",
        "File",
      ],
    }),
    patchPartABPlanCoverage: build.mutation<
      unknown,
      PatchPartABPlanCoverageArgs
    >({
      query: ({ id, data }) => ({
        url: `api/v1/part-a-b-plan-coverage/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "MSA",
        "MSAValidation",
        "MCP",
        "LienFile",
        "PlanCoverage",
        "File",
      ],
    }),
    deletePartABPlanCoverage: build.mutation<
      void,
      DeletePartABPlanCoverageArgs
    >({
      query: ({ id }) => ({
        url: `api/v1/part-a-b-plan-coverage/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "MSA",
        "MSAValidation",
        "MCP",
        "LienFile",
        "PlanCoverage",
        "File",
      ],
    }),
    createPartCDPlanCoverage: build.mutation<
      unknown,
      CreatePartCDPlanCoverageArgs
    >({
      query: ({ data }) => ({
        url: `api/v1/part-c-d-plan-coverage/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        "MSA",
        "MSAValidation",
        "MCP",
        "LienFile",
        "PlanCoverage",
        "File",
      ],
    }),
    patchPartCDPlanCoverage: build.mutation<
      unknown,
      PatchPartCDPlanCoverageArgs
    >({
      query: ({ id, data }) => ({
        url: `api/v1/part-c-d-plan-coverage/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "MSA",
        "MSAValidation",
        "MCP",
        "LienFile",
        "PlanCoverage",
        "File",
      ],
    }),
    deletePartCDPlanCoverage: build.mutation<
      void,
      DeletePartCDPlanCoverageArgs
    >({
      query: ({ id }) => ({
        url: `api/v1/part-c-d-plan-coverage/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "MSA",
        "MSAValidation",
        "MCP",
        "LienFile",
        "PlanCoverage",
        "File",
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPartABPlanCoveragesQuery,
  useGetPartCDPlanCoveragesQuery,
  useCreatePartABPlanCoverageMutation,
  usePatchPartABPlanCoverageMutation,
  useDeletePartABPlanCoverageMutation,
  useCreatePartCDPlanCoverageMutation,
  usePatchPartCDPlanCoverageMutation,
  useDeletePartCDPlanCoverageMutation,
} = partCoverageAPI;
